import React, { memo, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    Stack,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AddLandingPageSteps } from "../../AddEditLandingPage.types";
import StepContainer from "../../components/StepContainer";
import useUpdateEffect from "hooks/useUpdateEffect";

// ✅ Yup Validation Schema
const schema = yup.object().shape({
    name: yup.string().required("Name is required"),
    code: yup
        .string()
        .required("Unique code is required")
        .max(50, "Unique code must be at most 50 characters"),
    auto_redirect: yup.string().oneOf(["disabled", "immediately", "delayed"]),
    redirect_time: yup
        .number()
        .nullable()
        .when("auto_redirect", {
            is: "delayed",
            then: (schema) =>
                schema
                    .required("Delay time required")
                    .min(10, "Min is 10")
                    .max(1800, "Max is 1800"),
            otherwise: (schema) => schema.notRequired(),
        }),
});

export default memo(function GeneralStep({
    onSave,
    setIsStepDirty,
    form,
}) {

    const {
        control,
        handleSubmit,
        watch,
        reset,
        trigger,
        formState: { errors, isDirty },
    } = useForm({
        defaultValues: {
            name: "",
            code: "", // auto-generated, disabled field
            auto_redirect: "disabled",
            redirect_time: 0,
            ...form,
        },
        resolver: yupResolver(schema),
    });
    const onSubmit = (data) => {
        const { auto_redirect, redirect_time, ...rest } = data;
        const delayTime = auto_redirect === "immediately" ? 1 : redirect_time;
        onSave({
            stepName: AddLandingPageSteps.GENERAL,
            data: {
                ...rest,
                ...(auto_redirect === "disabled"
                    ? { redirect_time: 0 }
                    : { redirect_time: delayTime }),
            },
        });
    };

    const redirectValue = watch("auto_redirect");

    useEffect(() => {
        if (!form) return;
        const { redirect_time } = form;
        const autoRedirect =
            redirect_time === 0
                ? "disabled"
                : redirect_time === 1
                ? "immediately"
                : "delayed";
        reset({
            ...form,
            ...(redirect_time !== undefined && redirect_time !== null
                ? {
                      auto_redirect: autoRedirect,
                      redirect_time: redirect_time,
                  }
                : {}),
        });
    }, [form]);

    useUpdateEffect(() => {
        setIsStepDirty(isDirty);
    }, [isDirty]);

    return (
        <StepContainer
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isDirty={isDirty}
        >
            {/* Step Header */}
            <Box sx={{ mb: { xs: 3, md: 3 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: "24px", sm: "28px", md: "32px" },
                        mb: { xs: 1, md: 2 },
                        textAlign: { xs: "center", md: "left" },
                    }}
                >
                    General Information
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        textAlign: { xs: "center", md: "left" },
                        lineHeight: 1.5,
                    }}
                >
                    Set up the basic details for your landing page
                </Typography>
            </Box>

            {/* Form Fields */}
            <Stack spacing={{ xs: 3, md: 4 }} sx={{ width: "100%" }}>
                <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                        <Box>
                            <TextField
                                label="Name"
                                fullWidth
                                {...field}
                                error={!!errors.name}
                                helperText={
                                    errors.name?.message ||
                                    "Use a name that reflects the date or theme of your campaign."
                                }
                                sx={{
                                    "& .MuiInputBase-root": {
                                        height: { xs: "48px", md: "56px" },
                                        fontSize: { xs: "16px", md: "18px" },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        fontSize: { xs: "12px", md: "14px" },
                                        mt: { xs: 0.5, md: 1 },
                                    },
                                }}
                            />
                        </Box>
                    )}
                />

                {/* Unique Code (disabled) */}
                <Controller
                    name="code"
                    control={control}
                    render={({ field }) => (
                        <Box>
                            <TextField
                                label="Unique Code"
                                fullWidth
                                {...field}
                                error={!!errors.code}
                                helperText={
                                    errors.code?.message ||
                                    "This is the unique ID for your page. It's auto-generated."
                                }
                                sx={{
                                    "& .MuiInputBase-root": {
                                        height: { xs: "48px", md: "56px" },
                                        fontSize: { xs: "16px", md: "18px" },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        fontSize: { xs: "12px", md: "14px" },
                                        mt: { xs: 0.5, md: 1 },
                                    },
                                }}
                            />
                        </Box>
                    )}
                />

                {/* Auto Redirect */}
                <Box>
                    <FormControl fullWidth error={!!errors.auto_redirect}>
                        <Controller
                            name="auto_redirect"
                            control={control}
                            defaultValue="disabled"
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    value={field.value || "disabled"}
                                    sx={{
                                        height: { xs: "48px", md: "56px" },
                                        fontSize: { xs: "16px", md: "18px" },
                                    }}
                                >
                                    <MenuItem value="disabled">
                                        Disabled
                                    </MenuItem>
                                    <MenuItem value="immediately">
                                        Immediately
                                    </MenuItem>
                                    <MenuItem value="delayed">Delayed</MenuItem>
                                </Select>
                            )}
                        />
                        <FormHelperText
                            sx={{
                                fontSize: { xs: "12px", md: "14px" },
                                mt: { xs: 0.5, md: 1 },
                                lineHeight: 1.4,
                            }}
                        >
                            Set the redirect delay after form submission.
                            Options: Disabled, Immediately, or Delayed (10 –
                            1800 sec).
                        </FormHelperText>
                    </FormControl>
                </Box>

                {/* Delay Slider (only when "Delayed" selected) */}
                {redirectValue === "delayed" && (
                    <Box>
                        <Controller
                            name="redirect_time"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    error={!!errors.redirect_time}
                                    helperText={
                                        errors.redirect_time?.message ||
                                        "The redirect time must be between 10 and 1800 seconds. This value defines how long a user should wait before being redirected to another page."
                                    }
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: { xs: "48px", md: "56px" },
                                            fontSize: {
                                                xs: "16px",
                                                md: "18px",
                                            },
                                        },
                                        "& .MuiFormHelperText-root": {
                                            fontSize: {
                                                xs: "12px",
                                                md: "14px",
                                            },
                                            mt: { xs: 0.5, md: 1 },
                                            lineHeight: 1.4,
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
            </Stack>
        </StepContainer>
    );
});
